import { useCallback, useEffect, useRef, useState } from "react";
import { getUniqueId } from "@jugl-web/utils";
import { Subject } from "rxjs";
import { useRestApiProvider } from "../../../RestApiProvider";
import type {
  UsePaginatedRequestHookParams,
  UsePaginatedRequestHookReturn,
} from "../../../types";
import { Workflow } from "../models";

export const boardAutomationListUpdates$ = new Subject<
  | {
      type: "update";
      automation: Workflow;
    }
  | { type: "prepend"; automation: Workflow }
  | { type: "delete"; automationId: string }
>();

export type UseBoardAutomationsParams = UsePaginatedRequestHookParams<{
  entityId: string;
}>;

export type UseBoardAutomationsReturn = UsePaginatedRequestHookReturn<Workflow>;

export const useBoardAutomations = ({
  entityId,
  pageSize = 10,
}: UseBoardAutomationsParams): UseBoardAutomationsReturn => {
  const { integrationsApi } = useRestApiProvider();
  const requestId = useRef<string>();

  const [automations, setAutomations] = useState<Workflow[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [getWorkflows] = integrationsApi.useLazyGetWorkflowsQuery();

  const loadPage = useCallback(
    async (page: number) => {
      try {
        setIsError(false);
        setIsLoading(true);

        const newRequestId = getUniqueId();
        requestId.current = newRequestId;

        const result = await getWorkflows({
          entityId,
          page,
          page_size: pageSize,
        }).unwrap();

        if (requestId.current !== newRequestId) return;

        if (result) {
          setAutomations((prev) => {
            if (page === 1) {
              return result.data;
            }

            const existingIds = new Set(
              prev.map((automation) => automation.id)
            );
            const newAutomations = result.data.filter(
              (automation) => !existingIds.has(automation.id)
            );

            return [...prev, ...newAutomations];
          });

          setTotalPages(result.total_pages);
          setCurrentPage(page);
        }
      } catch (error) {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [getWorkflows, entityId, pageSize]
  );

  const loadMore = useCallback(async () => {
    if (isLoading) return;
    if (totalPages !== null && currentPage >= totalPages) return;
    await loadPage(currentPage + 1);
  }, [isLoading, currentPage, totalPages, loadPage]);

  const reset = useCallback(() => {
    setAutomations([]);
    setCurrentPage(1);
    setTotalPages(null);
    setIsError(false);
    loadPage(1);
  }, [loadPage]);

  useEffect(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    const subscription = boardAutomationListUpdates$.subscribe((action) => {
      if (action.type === "prepend") {
        const { automation } = action;
        setAutomations((prev) => {
          const filtered = prev.filter((a) => a.id !== automation.id);

          return [automation, ...filtered];
        });
      } else if (action.type === "update") {
        const { automation } = action;
        setAutomations((prev) => {
          const existingIndex = prev.findIndex((a) => a.id === automation.id);

          if (existingIndex >= 0) {
            const newAutomations = [...prev];
            newAutomations[existingIndex] = automation;
            return newAutomations;
          }

          return prev;
        });
      } else if (action.type === "delete") {
        const { automationId } = action;
        setAutomations((prev) => prev.filter(({ id }) => id !== automationId));
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const retry = useCallback(async () => {
    const pageToRetry =
      currentPage === 1 && automations.length === 0 ? 1 : currentPage + 1;
    await loadPage(pageToRetry);
  }, [currentPage, automations.length, loadPage]);

  const hasMore = totalPages !== null && currentPage < totalPages;
  const isLoadingMore = isLoading && currentPage > 1;
  const isLoadingMoreError =
    isError && (currentPage > 1 || automations.length === 0);

  return {
    data: automations,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    loadMore,
    reset,
    retry,
  };
};
